import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getPlayerSession, setPlayerSession, clearPlayerSession } from '../session';
import { useGameSocket } from '../ws/useGameSocket';
import { useGameStore } from '../store';
import { useCountdown } from '../hooks/useCountdown';
import { Action } from '../shared/protocol';
import { useT } from '../i18n';

export function Play() {
  const t = useT();
  const navigate = useNavigate();
  const { code: codeParam } = useParams<{ code: string }>();
  const code = (codeParam ?? '').trim().toUpperCase();

  // Nickname is the gate: known from a prior session for THIS game, or prompted.
  const [nickname, setNickname] = useState(() => {
    const s = getPlayerSession();
    return s && s.code === code ? s.nickname : '';
  });
  const haveNick = nickname.length > 0;

  const socket = useGameSocket(haveNick ? { role: 'player' } : null);
  const { connected, state, calledOn, lastResult, error } = useGameStore();
  const prevConnected = useRef(false);

  const open = state?.questionState === 'open';
  const remaining = useCountdown(open ? state?.endsAt : undefined);

  // (Re)join whenever the socket transitions to connected — including after a
  // reconnect. The persisted playerId re-attaches to the same player and score.
  useEffect(() => {
    if (connected && !prevConnected.current && socket && haveNick) {
      const s = getPlayerSession();
      socket.send(Action.PlayerJoin, { code, nickname, playerId: s?.code === code ? s.playerId : undefined });
    }
    prevConnected.current = connected;
  }, [connected, socket, haveNick, code, nickname]);

  useEffect(() => {
    if (!code) navigate('/', { replace: true });
  }, [code, navigate]);

  if (!code) return null;

  // Nickname prompt (e.g. arriving fresh via a scanned QR).
  if (!haveNick) {
    return <NicknameGate code={code} onReady={setNickname} />;
  }

  const q = state?.question;
  const iBuzzed = state?.queue.some((e) => e.nickname === nickname);

  const leave = () => {
    clearPlayerSession();
    navigate('/');
  };

  return (
    <div className="stack">
      <div className="rowbetween">
        <span className="pill">{nickname}</span>
        <span className={connected ? 'status ok' : 'status bad'}>{connected ? t('play.connected') : t('play.reconnecting')}</span>
      </div>

      {error && <div className="banner error">{t(error)}</div>}

      {!state && <div className="card centered"><p className="muted">{t('play.joining', { code })}</p></div>}

      {state?.status === 'lobby' && (
        <div className="card centered">
          <h2>{t('play.youreIn')}</h2>
          <p className="muted">{t('play.waitingHost')}</p>
        </div>
      )}

      {state?.status === 'active' && (
        <div className="card">
          <div className="muted small">{state.topicTitle}</div>
          {q ? (
            <>
              <h2 className="question">{q.text}</h2>
              <div className="rowbetween">
                <span className="points">{t('play.pts', { n: q.points })}</span>
                {open && <span className="timer">{remaining}s</span>}
              </div>

              {open && (
                <button
                  className={`buzzer ${iBuzzed ? 'buzzed' : ''}`}
                  disabled={iBuzzed}
                  onClick={() => socket?.send(Action.PlayerIKnow)}
                >
                  {iBuzzed ? t('play.youBuzzed') : t('play.iKnow')}
                </button>
              )}
              {!open && state.questionState === 'closed' && <p className="muted">{t('play.buzzersClosed')}</p>}
            </>
          ) : state.questionState === 'idle' ? (
            // Question is staged but the host hasn't opened the buzzer yet — the
            // server withholds the text until then, so players just get ready.
            <p className="muted">{t('play.getReady')}</p>
          ) : (
            <p className="muted">{t('play.waitingNext')}</p>
          )}

          {calledOn && (
            <div className="banner">{t('play.calledOn', { name: calledOn.nickname })}</div>
          )}
          {lastResult && (
            <div className={`banner ${lastResult.correct ? 'success' : 'error'}`}>
              {lastResult.correct
                ? t('play.correct', { name: lastResult.nickname, points: lastResult.points })
                : t('play.wrong', { name: lastResult.nickname })}
            </div>
          )}

          <Queue nicknames={(state.queue ?? []).map((e) => e.nickname)} />
        </div>
      )}

      {state?.status === 'finished' && <FinalScores />}

      <Scoreboard />
      <button className="ghost" onClick={leave}>{t('play.leave')}</button>
    </div>
  );
}

function NicknameGate({ code, onReady }: { code: string; onReady: (nickname: string) => void }) {
  const t = useT();
  const [value, setValue] = useState('');
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const n = value.trim();
    if (!n) return;
    const prev = getPlayerSession();
    const playerId = prev && prev.code === code ? prev.playerId : undefined;
    setPlayerSession({ code, nickname: n, playerId });
    onReady(n);
  };
  return (
    <div className="card centered">
      <h1>{t('join.title')}</h1>
      <p className="muted">{t('join.pickNickname', { code })}</p>
      <form onSubmit={submit} className="stack">
        <label>
          {t('join.nickname')}
          <input value={value} onChange={(e) => setValue(e.target.value)} placeholder={t('join.namePlaceholder')} maxLength={24} autoFocus />
        </label>
        <button type="submit" className="primary" disabled={!value.trim()}>{t('join.submit')}</button>
      </form>
    </div>
  );
}

function Queue({ nicknames }: { nicknames: string[] }) {
  const t = useT();
  if (nicknames.length === 0) return null;
  return (
    <div className="queue">
      <div className="muted small">{t('play.buzzedIn')}</div>
      <ol>{nicknames.map((n, i) => <li key={i}>{n}</li>)}</ol>
    </div>
  );
}

function Scoreboard() {
  const t = useT();
  // Select the stable reference (not a fresh array) to avoid an infinite
  // re-render loop in zustand v5 / useSyncExternalStore.
  const scores = useGameStore((s) => s.state?.scores) ?? [];
  if (scores.length === 0) return null;
  return (
    <div className="card">
      <h3>{t('play.scores')}</h3>
      <ul className="scores">
        {scores.map((s) => (
          <li key={s.playerId}><span>{s.nickname}</span><span className="points">{s.score}</span></li>
        ))}
      </ul>
    </div>
  );
}

function FinalScores() {
  const t = useT();
  const scores = useGameStore((s) => s.state?.scores) ?? [];
  return (
    <div className="card centered">
      <h2>{t('play.gameOver')}</h2>
      {scores[0] && <p className="winner">{t('play.winner', { name: scores[0].nickname, score: scores[0].score })}</p>}
    </div>
  );
}
